"use client";

import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Activity, Send, Clock, SkipForward } from "lucide-react";

function AnimatedProgress() {
    const [progress, setProgress] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true });

    useEffect(() => {
        if (!isInView) return;
        const timer = setInterval(() => {
            setProgress((p) => {
                if (p >= 87) { clearInterval(timer); return 87; }
                return p + 1;
            });
        }, 40);
        return () => clearInterval(timer);
    }, [isInView]);

    return (
        <div ref={ref} className="space-y-2">
            <div className="flex justify-between items-center">
                <span className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em]">Live Trajectory</span>
                <span className="text-sm font-black text-white">{progress}%</span>
            </div>
            <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                <motion.div
                    className="h-full bg-gradient-to-r from-indigo-600 via-violet-500 to-fuchsia-500 rounded-full relative"
                    style={{ width: `${progress}%` }}
                >
                    <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.2),transparent)] animate-pulse" />
                </motion.div>
            </div>
        </div>
    );
}

const mockLogs = [
    { msg: "Email sent to john@acme.com", type: "success", id: "msg-abc123" },
    { msg: "Email sent to sarah@tech.io", type: "success", id: "msg-def456" },
    { msg: "Skipped invalid: bounce@fake.xyz", type: "failed", id: "msg-ghi789" },
    { msg: "Email sent to mike@startup.co", type: "success", id: "msg-jkl012" },
    { msg: "Email sent to lisa@corp.com", type: "success", id: "msg-mno345" },
];

export default function LivePreview() {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section ref={ref} className="relative py-24 px-6">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-50/50 to-transparent dark:via-gray-900/30" />

            <div className="relative max-w-5xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400 text-xs font-bold uppercase tracking-wider mb-4">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse mr-2" />
                        Live Preview
                    </span>
                    <h2 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-white tracking-tight">
                        Watch Your Campaign{" "}
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-rose-500 to-orange-500">
                            Come Alive
                        </span>
                    </h2>
                </motion.div>

                {/* Dashboard mockup */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="rounded-3xl bg-zinc-950 border border-white/10 shadow-2xl overflow-hidden"
                >
                    {/* Top bar */}
                    <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
                        <div className="flex gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-500/80" />
                            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                            <div className="w-3 h-3 rounded-full bg-green-500/80" />
                        </div>
                        <div className="flex-1 flex justify-center">
                            <div className="px-3 py-1 rounded-md bg-white/5 text-[10px] text-zinc-500 font-medium">
                                icomm.app/campaigns/live
                            </div>
                        </div>
                    </div>

                    <div className="p-6 space-y-5">
                        {/* Status badge */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-500/10 text-rose-400 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-rose-500/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                                Live
                            </div>
                            <span className="text-[10px] text-zinc-600 font-medium">
                                Apr 03, 2026 · 11:05:00
                            </span>
                        </div>

                        {/* Stats grid */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {[
                                { label: "Recipients", value: "2,450", icon: Send, color: "from-indigo-500 to-blue-500" },
                                { label: "Success", value: "2,134", icon: CheckCircle2, color: "from-emerald-500 to-teal-500" },
                                { label: "Skipped", value: "11", icon: SkipForward, color: "from-amber-500 to-teal-500" },
                                { label: "Failed", value: "23", icon: XCircle, color: "from-rose-500 to-orange-500" },
                                { label: "Remaining", value: "282", icon: Clock, color: "from-amber-500 to-yellow-500" },
                            ].map((stat, i) => (
                                <motion.div
                                    key={stat.label}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ delay: 0.5 + i * 0.1 }}
                                    className="p-4 rounded-2xl bg-white/[0.03] border border-white/5"
                                >
                                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center mb-2`}>
                                        <stat.icon className="h-3.5 w-3.5 text-white" />
                                    </div>
                                    <p className="text-[9px] text-zinc-600 font-bold uppercase tracking-widest">{stat.label}</p>
                                    <p className="text-xl font-bold text-white tracking-tight">{stat.value}</p>
                                </motion.div>
                            ))}
                        </div>

                        {/* Progress */}
                        <div className="p-5 rounded-2xl bg-zinc-900 border border-white/5">
                            <AnimatedProgress />
                        </div>

                        {/* Activity Feed */}
                        <div className="rounded-2xl border border-white/5 overflow-hidden">
                            <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
                                <Activity className="h-3.5 w-3.5 text-indigo-400" />
                                <span className="text-xs text-zinc-300 font-bold">Frequency Stream</span>
                            </div>
                            <div className="p-4 space-y-2 max-h-[180px] overflow-hidden">
                                {mockLogs.map((log, i) => (
                                    <motion.div
                                        key={log.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                                        transition={{ delay: 0.8 + i * 0.12 }}
                                        className={`flex items-center gap-3 p-3 rounded-xl border ${log.type === "success"
                                            ? "border-emerald-500/10 bg-emerald-500/[0.02]"
                                            : "border-rose-500/10 bg-rose-500/[0.02]"
                                            }`}
                                    >
                                        <div className={`p-1.5 rounded-lg ${log.type === "success" ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400"}`}>
                                            {log.type === "success" ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                        </div>
                                        <span className={`text-xs font-semibold ${log.type === "success" ? "text-emerald-400" : "text-rose-400"}`}>
                                            {log.msg}
                                        </span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
